/**
 * @fileoverview Dexie system database definition for the system-web application.
 * @module local-db/system-db
 *
 * Responsibilities:
 * - Define the system Dexie schema and exposed collection surface.
 * - Provide singleton access helpers for the system-web local database.
 *
 * Dependencies:
 * - dexie
 * - ./tenantDb
 *
 * Architecture:
 * - System-web uses Dexie as the local-first source of truth for synced system collections.
 * - Library/reference data such as raw code set artifacts are stored locally and hydrated via sync.
 *
 * Notes:
 * - System admin data is not encrypted in Dexie.
 */

import Dexie from 'dexie';
import { DexieCollection } from './tenantDb';
import type { MigrationMeta } from '../types/dbMeta';
import {
  SYSTEM_EXPECTED_DB_SCHEMA_VERSION,
  SYSTEM_MIN_MIGRATABLE_VERSION,
} from '@firesquirrel/sync-config';

/**
 * @storage firesquirrel-system
 *
 * Type:
 * - IndexedDB
 *
 * Schema:
 * - System collections including tenants, reports, templates, forms, uploads, and `code_set_raw_artifacts`.
 *
 * Sync Role:
 * - Local cache for system-web reads and background sync replication.
 */

// ---------------------------------------------------------------------------
// Schema version constants — re-exported from @firesquirrel/sync-config
// ---------------------------------------------------------------------------

/**
 * The current Dexie schema version the code expects. The localDatabaseManager
 * compares this against the `_db_meta.schemaVersion` in the local DB and the
 * server's `local_db_metadata.schemaVersion` to determine recovery action.
 *
 * When a developer adds a new `db.version(N)` they MUST update the constant in
 * `api/packages/sync-config/src/local-db-versions.ts` to match the new version
 * number. The local-db-schema-update skill enforces this.
 *
 * The canonical constant lives in `@firesquirrel/sync-config` so both the
 * frontend (localDatabaseManager) and the API (localDbMetadataService) share
 * a single source of truth — no drift between what the code compiled and what
 * the API seeded in MongoDB.
 */
export const EXPECTED_DB_SCHEMA_VERSION = SYSTEM_EXPECTED_DB_SCHEMA_VERSION;

/**
 * The oldest schema version for which migration scripts exist. If the local DB
 * is older than this, the manager wipes and rebuilds instead of attempting
 * incremental migration (the upgrade functions for older versions may not
 * exist in the current code bundle).
 *
 * Update this ONLY if a new migration requires data that only exists from a
 * newer version onward. Otherwise leave it — old migration scripts remain valid.
 *
 * Canonical source: `@firesquirrel/sync-config` (`SYSTEM_MIN_MIGRATABLE_VERSION`).
 */
export const MIN_MIGRATABLE_VERSION = SYSTEM_MIN_MIGRATABLE_VERSION;

/**
 * Per-version migration metadata. The localDatabaseManager reads this to
 * determine whether writes should be held during a migration range.
 *
 * Phase 8 (deferred): entries with `holdWrites: true` will block writes until
 * the migration AND full re-sync complete. For Phase 1, all migrations default
 * to `holdWrites: false` (writes resume immediately after the Dexie upgrade).
 */
export const MIGRATION_REGISTRY: Record<number, MigrationMeta> = {
  27: { holdWrites: false, reason: 'Enforce uniqueness on geo_cache_files (clears data, server repopulates)' },
  28: { holdWrites: false, reason: 'One-time clear of stale geo_cache_files data' },
  29: { holdWrites: false, reason: 'Add _db_meta table for local database identity' },
  30: { holdWrites: false, reason: 'Add updatedAt index to geo_cache_files for replication delta sync' },
};

// ---------------------------------------------------------------------------
// Dexie schema — current system collections, plain tables (no SecureTable)
// ---------------------------------------------------------------------------
function createDexieInstance(): Dexie {
  const db = new Dexie('firesquirrel-system');
  db.version(17).stores({
    tenants:                       '_id, status, tier, updatedAt',
    organizations:                 '_id, status, updatedAt',
    system_users:                  '_id, email, status, updatedAt',
    system_user_groups:            '_id, updatedAt',
    system_roles:                  '_id, status, updatedAt',
    organization_roles:            '_id, updatedAt',
    tiers:                         '_id, code, updatedAt',
    feature_flags:                 '_id, key, status, updatedAt',
    modules:                       '_id, code, status, updatedAt',
    system_lookups:                '_id, lookupType, updatedAt',
    system_lookup_groups:          '_id, lookupType, updatedAt',
    system_lookup_categories:      '_id, updatedAt',
    onboarding_drafts:             '_id, updatedAt',
    import_templates:              '_id, dataMode, visibility, status, parentTemplateId, updatedAt',
    import_runs:                   '_id, status, startedAt, tenantId, updatedAt',
    report_definitions:            '_id, type, status, updatedAt',
    report_templates:              '_id, documentType, status, isInLibrary, visibility, updatedAt',
    report_schedules:              '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    email_templates:               '_id, status, isInLibrary, visibility, updatedAt',
    uploaded_files:                '_id, mimeType, category, updatedAt',
    generated_documents:           '_id, reportDefinitionId, status, updatedAt',
    code_set_raw_artifacts:        '_id, cacheKey, vendorId, bookId, printing, status, downloadedAt, updatedAt',
    fire_boundaries:               '_id, nerisId, stateCode, updatedAt, cachedAt',
    system_inspection_templates:   '_id, templateType, status, updatedAt',
    audit_logs:                    '_id, collection, documentId, action, createdAt',
    system_forms:                  '_id, templateType, status, reusable, updatedAt',
    _sync_meta:                    '_id',
  });
  // ---------------------------------------------------------------------------
  // Version 18 — Add `background_jobs` for the tether indicator + background
  // operations flyout. Server-authoritative (workers write; clients read only).
  // Indexes support UI queries: active jobs by status, filter by jobType, and
  // sort by startedAt for the flyout "active + recently finished" view.
  // ---------------------------------------------------------------------------
  db.version(18).stores({
    tenants:                       '_id, status, tier, updatedAt',
    organizations:                 '_id, status, updatedAt',
    system_users:                  '_id, email, status, updatedAt',
    system_user_groups:            '_id, updatedAt',
    system_roles:                  '_id, status, updatedAt',
    organization_roles:            '_id, updatedAt',
    tiers:                         '_id, code, updatedAt',
    feature_flags:                 '_id, key, status, updatedAt',
    modules:                       '_id, code, status, updatedAt',
    system_lookups:                '_id, lookupType, updatedAt',
    system_lookup_groups:          '_id, lookupType, updatedAt',
    system_lookup_categories:      '_id, updatedAt',
    onboarding_drafts:             '_id, updatedAt',
    import_templates:              '_id, dataMode, visibility, status, parentTemplateId, updatedAt',
    import_runs:                   '_id, status, startedAt, tenantId, updatedAt',
    report_definitions:            '_id, type, status, updatedAt',
    report_templates:              '_id, documentType, status, isInLibrary, visibility, updatedAt',
    report_schedules:              '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    email_templates:               '_id, status, isInLibrary, visibility, updatedAt',
    uploaded_files:                '_id, mimeType, category, updatedAt',
    generated_documents:           '_id, reportDefinitionId, status, updatedAt',
    code_set_raw_artifacts:        '_id, cacheKey, vendorId, bookId, printing, status, downloadedAt, updatedAt',
    fire_boundaries:               '_id, nerisId, stateCode, updatedAt, cachedAt',
    system_inspection_templates:   '_id, templateType, status, updatedAt',
    audit_logs:                    '_id, collection, documentId, action, createdAt',
    system_forms:                  '_id, templateType, status, reusable, updatedAt',
    background_jobs:               '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
    _sync_meta:                    '_id',
  });
  // ---------------------------------------------------------------------------
  // Version 19 — Add legacy migration tables for Streamline → FireSight migration
  // framework (Phase 0, LM-013). All three tables are server-authoritative mirrors:
  // system-api writes via the migration runner and manifest-override endpoints;
  // system-web reads via system sync + liveQuery. Push is NOT allowed for any of
  // these tables (read-only client sync policy, §7.2 of implementation plan).
  //
  // Tables:
  //   legacy_migration_receipts           — one doc per migrated legacy row
  //   legacy_migration_runs               — one doc per migration run attempt
  //   legacy_migration_manifest_overrides — one doc per dataset key (global)
  // ---------------------------------------------------------------------------
  db.version(19).stores({
    tenants:                               '_id, status, tier, updatedAt',
    organizations:                         '_id, status, updatedAt',
    system_users:                          '_id, email, status, updatedAt',
    system_user_groups:                    '_id, updatedAt',
    system_roles:                          '_id, status, updatedAt',
    organization_roles:                    '_id, updatedAt',
    tiers:                                 '_id, code, updatedAt',
    feature_flags:                         '_id, key, status, updatedAt',
    modules:                               '_id, code, status, updatedAt',
    system_lookups:                        '_id, lookupType, updatedAt',
    system_lookup_groups:                  '_id, lookupType, updatedAt',
    system_lookup_categories:              '_id, updatedAt',
    onboarding_drafts:                     '_id, updatedAt',
    import_templates:                      '_id, dataMode, visibility, status, parentTemplateId, updatedAt',
    import_runs:                           '_id, status, startedAt, tenantId, updatedAt',
    report_definitions:                    '_id, type, status, updatedAt',
    report_templates:                      '_id, documentType, status, isInLibrary, visibility, updatedAt',
    report_schedules:                      '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    email_templates:                       '_id, status, isInLibrary, visibility, updatedAt',
    uploaded_files:                        '_id, mimeType, category, updatedAt',
    generated_documents:                   '_id, reportDefinitionId, status, updatedAt',
    code_set_raw_artifacts:                '_id, cacheKey, vendorId, bookId, printing, status, downloadedAt, updatedAt',
    fire_boundaries:                       '_id, nerisId, stateCode, updatedAt, cachedAt',
    system_inspection_templates:           '_id, templateType, status, updatedAt',
    audit_logs:                            '_id, collection, documentId, action, createdAt',
    system_forms:                          '_id, templateType, status, reusable, updatedAt',
    background_jobs:                       '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
    // LM-013: Legacy migration tables (server-authoritative mirrors, read-only client sync)
    legacy_migration_receipts:             '_id, tenantId, datasetKey, legacyDb, legacySourceId, [tenantId+datasetKey], _deleted',
    legacy_migration_runs:                 '_id, tenantId, datasetKey, startedAt, [tenantId+datasetKey], _deleted',
    legacy_migration_manifest_overrides:   '_id, datasetKey, version, updatedAt, _deleted',
    _sync_meta:                            '_id',
  });
  // ---------------------------------------------------------------------------
  // Version 20 — Add `legacy_schema_manifest` for the schema-first migration
  // manifest editor. One document per legacy database (e.g. SLInspectionsDB001).
  // Stores all discovered tables from INFORMATION_SCHEMA, which ones the admin
  // has disabled, and admin-declared FK relationships. Server-authoritative;
  // clients POST edits via HTTP, read back via system sync pull + liveQuery.
  // This is the raw material for building named migration datasets.
  // ---------------------------------------------------------------------------
  db.version(20).stores({
    tenants:                               '_id, status, tier, updatedAt',
    organizations:                         '_id, status, updatedAt',
    system_users:                          '_id, email, status, updatedAt',
    system_user_groups:                    '_id, updatedAt',
    system_roles:                          '_id, status, updatedAt',
    organization_roles:                    '_id, updatedAt',
    tiers:                                 '_id, code, updatedAt',
    feature_flags:                         '_id, key, status, updatedAt',
    modules:                               '_id, code, status, updatedAt',
    system_lookups:                        '_id, lookupType, updatedAt',
    system_lookup_groups:                  '_id, lookupType, updatedAt',
    system_lookup_categories:              '_id, updatedAt',
    onboarding_drafts:                     '_id, updatedAt',
    import_templates:                      '_id, dataMode, visibility, status, parentTemplateId, updatedAt',
    import_runs:                           '_id, status, startedAt, tenantId, updatedAt',
    report_definitions:                    '_id, type, status, updatedAt',
    report_templates:                      '_id, documentType, status, isInLibrary, visibility, updatedAt',
    report_schedules:                      '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    email_templates:                       '_id, status, isInLibrary, visibility, updatedAt',
    uploaded_files:                        '_id, mimeType, category, updatedAt',
    generated_documents:                   '_id, reportDefinitionId, status, updatedAt',
    code_set_raw_artifacts:                '_id, cacheKey, vendorId, bookId, printing, status, downloadedAt, updatedAt',
    fire_boundaries:                       '_id, nerisId, stateCode, updatedAt, cachedAt',
    system_inspection_templates:           '_id, templateType, status, updatedAt',
    audit_logs:                            '_id, collection, documentId, action, createdAt',
    system_forms:                          '_id, templateType, status, reusable, updatedAt',
    background_jobs:                       '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
    // LM-013: Legacy migration tables (server-authoritative mirrors, read-only client sync)
    legacy_migration_receipts:             '_id, tenantId, datasetKey, legacyDb, legacySourceId, [tenantId+datasetKey], _deleted',
    legacy_migration_runs:                 '_id, tenantId, datasetKey, startedAt, [tenantId+datasetKey], _deleted',
    legacy_migration_manifest_overrides:   '_id, datasetKey, version, updatedAt, _deleted',
    // LM-020: Schema manifest — one doc per legacyDb, admin-shaped table/FK catalog
    _sync_meta:                            '_id',
  });
  db.version(21).stores({
    tenants:                               '_id, status, tier, updatedAt',
    organizations:                         '_id, status, updatedAt',
    system_users:                          '_id, email, status, updatedAt',
    system_user_groups:                    '_id, updatedAt',
    system_roles:                          '_id, status, updatedAt',
    organization_roles:                    '_id, updatedAt',
    tiers:                                 '_id, code, updatedAt',
    feature_flags:                         '_id, key, status, updatedAt',
    modules:                               '_id, code, status, updatedAt',
    system_lookups:                        '_id, lookupType, updatedAt',
    system_lookup_groups:                  '_id, lookupType, updatedAt',
    system_lookup_categories:              '_id, updatedAt',
    onboarding_drafts:                     '_id, updatedAt',
    import_templates:                      '_id, dataMode, visibility, status, parentTemplateId, updatedAt',
    import_runs:                           '_id, status, startedAt, tenantId, updatedAt',
    report_definitions:                    '_id, type, status, updatedAt',
    report_templates:                      '_id, documentType, status, isInLibrary, visibility, updatedAt',
    report_schedules:                      '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    email_templates:                       '_id, status, isInLibrary, visibility, updatedAt',
    uploaded_files:                        '_id, mimeType, category, updatedAt',
    generated_documents:                   '_id, reportDefinitionId, status, updatedAt',
    code_set_raw_artifacts:                '_id, cacheKey, vendorId, bookId, printing, status, downloadedAt, updatedAt',
    fire_boundaries:                       '_id, nerisId, stateCode, updatedAt, cachedAt',
    system_inspection_templates:           '_id, templateType, status, updatedAt',
    audit_logs:                            '_id, collection, documentId, action, createdAt',
    system_forms:                          '_id, templateType, status, reusable, updatedAt',
    background_jobs:                       '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
    legacy_migration_receipts:             '_id, tenantId, datasetKey, legacyDb, legacySourceId, [tenantId+datasetKey], _deleted',
    legacy_migration_runs:                 '_id, tenantId, datasetKey, startedAt, [tenantId+datasetKey], _deleted',
    legacy_migration_manifest_overrides:   '_id, datasetKey, version, updatedAt, _deleted',
    legacy_schema_manifest_tables:         '_id, legacyDb, schema, table, [legacyDb+schema+table], disabled, updatedAt, _deleted',
    _sync_meta:                            '_id',
  });

  // v22: Dataset Template System (§3.11.6 — G-086)
  // Adds legacy_migration_dataset_templates: one document per migration dataset template.
  // Server-authoritative: system-api writes via dataset-template routes; never client-pushed.
  // Indexes: status (grid filter dropdown), kind (system-known vs user-defined), updatedAt, _deleted.
  db.version(22).stores({
    tenants:                               '_id, status, tier, updatedAt',
    organizations:                         '_id, status, updatedAt',
    system_users:                          '_id, email, status, updatedAt',
    system_user_groups:                    '_id, updatedAt',
    system_roles:                          '_id, status, updatedAt',
    organization_roles:                    '_id, updatedAt',
    tiers:                                 '_id, code, updatedAt',
    feature_flags:                         '_id, key, status, updatedAt',
    modules:                               '_id, code, status, updatedAt',
    system_lookups:                        '_id, lookupType, updatedAt',
    system_lookup_groups:                  '_id, lookupType, updatedAt',
    system_lookup_categories:              '_id, updatedAt',
    onboarding_drafts:                     '_id, updatedAt',
    import_templates:                      '_id, dataMode, visibility, status, parentTemplateId, updatedAt',
    import_runs:                           '_id, status, startedAt, tenantId, updatedAt',
    report_definitions:                    '_id, type, status, updatedAt',
    report_templates:                      '_id, documentType, status, isInLibrary, visibility, updatedAt',
    report_schedules:                      '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    email_templates:                       '_id, status, isInLibrary, visibility, updatedAt',
    uploaded_files:                        '_id, mimeType, category, updatedAt',
    generated_documents:                   '_id, reportDefinitionId, status, updatedAt',
    code_set_raw_artifacts:                '_id, cacheKey, vendorId, bookId, printing, status, downloadedAt, updatedAt',
    fire_boundaries:                       '_id, nerisId, stateCode, updatedAt, cachedAt',
    system_inspection_templates:           '_id, templateType, status, updatedAt',
    audit_logs:                            '_id, collection, documentId, action, createdAt',
    system_forms:                          '_id, templateType, status, reusable, updatedAt',
    background_jobs:                       '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
    legacy_migration_receipts:             '_id, tenantId, datasetKey, legacyDb, legacySourceId, [tenantId+datasetKey], _deleted',
    legacy_migration_runs:                 '_id, tenantId, datasetKey, startedAt, [tenantId+datasetKey], _deleted',
    legacy_migration_manifest_overrides:   '_id, datasetKey, version, updatedAt, _deleted',
    legacy_schema_manifest_tables:         '_id, legacyDb, schema, table, [legacyDb+schema+table], disabled, updatedAt, _deleted',
    // G-086: Dataset template table (§3.11.6)
    legacy_migration_dataset_templates:    '_id, key, status, kind, updatedAt, _deleted',
    _sync_meta:                            '_id',
  });

  // v23: Clusters registry — server-authoritative, read-only client sync.
  // system-web reads cluster metadata (name, region, status) to display the
  // real Atlas cluster name on the department configuration tab instead of the
  // denormalized tenant.dataLocation.clusterName. The pull projection in
  // system-api syncRoutes strips `connectionString` so credentials never reach
  // the client. Indexes: name (lookup by display name), status (filter active),
  // updatedAt (sync cursor).
  db.version(23).stores({
    tenants:                               '_id, status, tier, updatedAt',
    organizations:                         '_id, status, updatedAt',
    system_users:                          '_id, email, status, updatedAt',
    system_user_groups:                    '_id, updatedAt',
    system_roles:                          '_id, status, updatedAt',
    organization_roles:                    '_id, updatedAt',
    tiers:                                 '_id, code, updatedAt',
    feature_flags:                         '_id, key, status, updatedAt',
    modules:                               '_id, code, status, updatedAt',
    system_lookups:                        '_id, lookupType, updatedAt',
    system_lookup_groups:                  '_id, lookupType, updatedAt',
    system_lookup_categories:              '_id, updatedAt',
    onboarding_drafts:                     '_id, updatedAt',
    import_templates:                      '_id, dataMode, visibility, status, parentTemplateId, updatedAt',
    import_runs:                           '_id, status, startedAt, tenantId, updatedAt',
    report_definitions:                    '_id, type, status, updatedAt',
    report_templates:                      '_id, documentType, status, isInLibrary, visibility, updatedAt',
    report_schedules:                      '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    email_templates:                       '_id, status, isInLibrary, visibility, updatedAt',
    uploaded_files:                        '_id, mimeType, category, updatedAt',
    generated_documents:                   '_id, reportDefinitionId, status, updatedAt',
    code_set_raw_artifacts:                '_id, cacheKey, vendorId, bookId, printing, status, downloadedAt, updatedAt',
    fire_boundaries:                       '_id, nerisId, stateCode, updatedAt, cachedAt',
    system_inspection_templates:           '_id, templateType, status, updatedAt',
    audit_logs:                            '_id, collection, documentId, action, createdAt',
    system_forms:                          '_id, templateType, status, reusable, updatedAt',
    background_jobs:                       '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
    legacy_migration_receipts:             '_id, tenantId, datasetKey, legacyDb, legacySourceId, [tenantId+datasetKey], _deleted',
    legacy_migration_runs:                 '_id, tenantId, datasetKey, startedAt, [tenantId+datasetKey], _deleted',
    legacy_migration_manifest_overrides:   '_id, datasetKey, version, updatedAt, _deleted',
    legacy_schema_manifest_tables:         '_id, legacyDb, schema, table, [legacyDb+schema+table], disabled, updatedAt, _deleted',
    legacy_migration_dataset_templates:    '_id, key, status, kind, updatedAt, _deleted',
    clusters:                              '_id, name, status, updatedAt',
    _sync_meta:                            '_id',
  });

  // v24: AI Model Registry — server-authoritative, read-only client sync.
  // system-web reads the AI model catalog (ai_models) and task→model
  // assignments (ai_task_assignments) for the Settings → AI Models admin UI.
  // Push is blocked — all writes go through system-api
  // /api/system/ai-models and /api/system/ai-task-assignments routes.
  // Indexes: deploymentName (uniqueness check), apiType (filter by chat/
  // vision/embedding), isActive (filter active models for assignment
  // dropdowns), taskKey (lookup by task), updatedAt (sync cursor).
  db.version(24).stores({
    tenants:                               '_id, status, tier, updatedAt',
    organizations:                         '_id, status, updatedAt',
    system_users:                          '_id, email, status, updatedAt',
    system_user_groups:                    '_id, updatedAt',
    system_roles:                          '_id, status, updatedAt',
    organization_roles:                    '_id, updatedAt',
    tiers:                                 '_id, code, updatedAt',
    feature_flags:                         '_id, key, status, updatedAt',
    modules:                               '_id, code, status, updatedAt',
    system_lookups:                        '_id, lookupType, updatedAt',
    system_lookup_groups:                  '_id, lookupType, updatedAt',
    system_lookup_categories:              '_id, updatedAt',
    onboarding_drafts:                     '_id, updatedAt',
    import_templates:                      '_id, dataMode, visibility, status, parentTemplateId, updatedAt',
    import_runs:                           '_id, status, startedAt, tenantId, updatedAt',
    report_definitions:                    '_id, type, status, updatedAt',
    report_templates:                      '_id, documentType, status, isInLibrary, visibility, updatedAt',
    report_schedules:                      '_id, reportDefinitionId, isActive, nextRunAt, updatedAt',
    email_templates:                       '_id, status, isInLibrary, visibility, updatedAt',
    uploaded_files:                        '_id, mimeType, category, updatedAt',
    generated_documents:                   '_id, reportDefinitionId, status, updatedAt',
    code_set_raw_artifacts:                '_id, cacheKey, vendorId, bookId, printing, status, downloadedAt, updatedAt',
    fire_boundaries:                       '_id, nerisId, stateCode, updatedAt, cachedAt',
    system_inspection_templates:           '_id, templateType, status, updatedAt',
    audit_logs:                            '_id, collection, documentId, action, createdAt',
    system_forms:                          '_id, templateType, status, reusable, updatedAt',
    background_jobs:                       '_id, status, jobType, scope, tenantId, [status+startedAt], startedAt, updatedAt',
    legacy_migration_receipts:             '_id, tenantId, datasetKey, legacyDb, legacySourceId, [tenantId+datasetKey], _deleted',
    legacy_migration_runs:                 '_id, tenantId, datasetKey, startedAt, [tenantId+datasetKey], _deleted',
    legacy_migration_manifest_overrides:   '_id, datasetKey, version, updatedAt, _deleted',
    legacy_schema_manifest_tables:         '_id, legacyDb, schema, table, [legacyDb+schema+table], disabled, updatedAt, _deleted',
    legacy_migration_dataset_templates:    '_id, key, status, kind, updatedAt, _deleted',
    clusters:                              '_id, name, status, updatedAt',
    ai_models:                             '_id, deploymentName, apiType, isActive, updatedAt',
    ai_task_assignments:                   '_id, taskKey, apiType, isActive, updatedAt',
    _sync_meta:                            '_id',
  });

  // v25: API usage cache (Phase 5.10) — NOT a synced collection. Unlike
  // ai_models/clusters above (which sync read-only via the standard pull
  // pipeline), cross-tenant usage meters are fetched on demand by
  // apiUsageService.ts (GET /api/system/usage) and cached here for the
  // Settings → API Usage admin UI to read via liveQuery. One row per
  // tenant, keyed by tenantId.
  db.version(25).stores({
    api_usage_cache:                       '_id, fetchedAt',
  });

  // v26: Geo-Import cache file metadata (Phase 7).
  // Regrid master-cache GeoParquet file metadata — synced from the system
  // MongoDB geoCacheFiles collection via the standard pull pipeline. One row
  // per county, keyed by the FIPS code so a re-upload for the same county
  // updates the same record. Includes state/county/FIPS indexes for grouping
  // and auto-match by the onboarding wizard.
  db.version(26).stores({
    geo_cache_files:                       '_id, state, county, fipsCode, fetchedAt',
  });

  // v27: Enforce uniqueness on geo_cache_files — mirrors the MongoDB unique
  // indexes. `&fipsCode` is the primary uniqueness constraint (FIPS is the
  // nationally unique 5-digit county code). `&[state+county]` is the
  // compound unique index matching the business definition of a duplicate.
  // The upgrade callback CLEARS ALL geo_cache_files data because the server
  // (MongoDB geoCacheFiles collection) is the source of truth and
  // refreshCacheFiles() will repopulate from it on the next mount. This
  // guarantees the unique indexes can always be created, even if the
  // previous local state had duplicate data from failed runs that would
  // otherwise violate the unique constraints and block the DB from opening.
  db.version(27).stores({
    geo_cache_files:                       '_id, state, county, &fipsCode, &[state+county], fetchedAt',
  }).upgrade(async (tx) => {
    const table = tx.table('geo_cache_files');
    const count = await table.count();
    if (count > 0) {
      console.info(
        `[systemDb] v27 upgrade — clearing ${count} geo_cache_files row(s); server is source of truth, refreshCacheFiles will repopulate`,
      );
      await table.clear();
    }
  });

  // v28: One-time clear of stale geo_cache_files data. v27's upgrade
  // callback was changed after it was already applied to some browsers, so
  // Dexie did not re-run it. This version bump forces the clear to execute
  // on every browser that already has v27 applied, removing any stale rows
  // left over from before the server-side cache reset. After this, the
  // schema is unchanged from v27 — only the data is cleared. The server is
  // the source of truth; refreshCacheFiles() repopulates on mount.
  db.version(28).stores({
    geo_cache_files:                       '_id, state, county, &fipsCode, &[state+county], fetchedAt',
  }).upgrade(async (tx) => {
    const table = tx.table('geo_cache_files');
    const count = await table.count();
    if (count > 0) {
      console.info(
        `[systemDb] v28 upgrade — clearing ${count} stale geo_cache_files row(s)`,
      );
      await table.clear();
    }
  });

  // v29: Add `_db_meta` table for local database identity (Local Database
  // Manager — Phase 1). This single-record table stores the database's
  // identity (dbId, environment, schemaVersion, appVersion, apiBaseUrl) so
  // the localDatabaseManager can detect cross-environment data bleed and
  // trigger recovery. The table is NOT in any sync config map — it is
  // written only by localDatabaseManager during creation or after recovery.
  // No data transformation needed — just adding the table. The
  // localDatabaseManager writes the initial `_db_meta` record on first open.
  db.version(29).stores({
    _db_meta: '_id',
  });

  // v30: Add `updatedAt` index to geo_cache_files. The replication factory
  // queries `table.where('updatedAt').above(lastCheckpoint)` for delta sync,
  // but geo_cache_files had no updatedAt index — every sync cycle threw
  // SchemaError, which Dexie logged to console even though the catch block
  // fell back to table.toArray(). This produced an endless flood of
  // DexieError2 console spam and prevented efficient delta pulls. Adding the
  // index is additive — Dexie builds it in the background on first open.
  db.version(30).stores({
    geo_cache_files: '_id, state, county, &fipsCode, &[state+county], fetchedAt, updatedAt',
  });

  // v31: #737 Phase 6 — inspection-program scheduling system catalogs.
  // `system_inspection_programs` mirrors the canonical 12-program catalog;
  // `system_schedule_rule_catalog` mirrors the rule-type catalog. Both are
  // server-authoritative (system-api routes own writes; replay propagates to
  // tenants) — system-web reads them local-first but never pushes. Additive.
  db.version(31).stores({
    system_inspection_programs: '_id, programKey, active, reoccurrence, division, updatedAt',
    system_schedule_rule_catalog: '_id, ruleTypeCode, active, updatedAt',
  });

  return db;
}

// ---------------------------------------------------------------------------
// SystemDatabase — shape returned by getSystemDb()
// ---------------------------------------------------------------------------
/**
 * @contract SystemCollections
 *
 * Purpose:
 * - Defines the wrapped Dexie collection surface available to system-web callers.
 *
 * Fields:
 * - Collection keys mirror system Dexie table names.
 *
 * Rules:
 * - Keys must stay aligned with the schema and TABLE_NAMES list.
 *
 * Sync:
 * - Collections are hydrated via system sync and read locally by system-web services.
 */
export type SystemCollections = {
  tenants: DexieCollection;
  organizations: DexieCollection;
  system_users: DexieCollection;
  system_user_groups: DexieCollection;
  system_roles: DexieCollection;
  organization_roles: DexieCollection;
  tiers: DexieCollection;
  feature_flags: DexieCollection;
  modules: DexieCollection;
  system_lookups: DexieCollection;
  system_lookup_groups: DexieCollection;
  system_lookup_categories: DexieCollection;
  onboarding_drafts: DexieCollection;
  import_templates: DexieCollection;
  import_runs: DexieCollection;
  report_definitions: DexieCollection;
  report_templates: DexieCollection;
  report_schedules: DexieCollection;
  email_templates: DexieCollection;
  uploaded_files: DexieCollection;
  generated_documents: DexieCollection;
  code_set_raw_artifacts: DexieCollection;
  fire_boundaries: DexieCollection;
  system_inspection_templates: DexieCollection;
  audit_logs: DexieCollection;
  system_forms: DexieCollection;
  background_jobs: DexieCollection;
  // LM-013: Legacy migration tables (server-authoritative mirrors, read-only client sync)
  legacy_migration_receipts: DexieCollection;
  legacy_migration_runs: DexieCollection;
  legacy_migration_manifest_overrides: DexieCollection;
  // LM-020: Schema manifest — one doc per legacy table, admin-shaped table/FK catalog
  legacy_schema_manifest_tables: DexieCollection;
  // G-086: Dataset Template System (§3.11.6) — server-authoritative, never client-pushed
  legacy_migration_dataset_templates: DexieCollection;
  // Clusters registry — server-authoritative, read-only client sync.
  // connectionString is stripped by the server pull projection.
  clusters: DexieCollection;
  // AI model registry — server-authoritative, read-only client sync.
  // ai_models: model catalog (deployments). ai_task_assignments: task→model mapping.
  // Push blocked — all writes go through system-api routes.
  ai_models: DexieCollection;
  ai_task_assignments: DexieCollection;
  // Phase 5.10 — local cache only, NOT synced via the standard pipeline.
  api_usage_cache: DexieCollection;
  // Regrid master-cache file registry — synced from system MongoDB geoCacheFiles.
  geo_cache_files: DexieCollection;
  // #737 Phase 6 — system-managed scheduling catalogs (pull-only mirrors;
  // writes go through system-api routes which trigger tenant propagation).
  system_inspection_programs: DexieCollection;
  system_schedule_rule_catalog: DexieCollection;
};

/**
 * @contract SystemDatabase
 *
 * Purpose:
 * - Represents the singleton system Dexie database wrapper returned to callers.
 *
 * Fields:
 * - collections: Named collection map for indexed access.
 * - close: Closes the underlying Dexie instance.
 *
 * Rules:
 * - Direct table access should go through these wrapped collections.
 *
 * Sync:
 * - Used by replication and local-first services in system-web.
 */
export interface SystemDatabase extends SystemCollections {
  collections: SystemCollections;
  close(): Promise<void>;
}

const TABLE_NAMES = [
  'tenants', 'organizations', 'system_users', 'system_user_groups',
  'system_roles', 'organization_roles', 'tiers', 'feature_flags',
  'modules', 'system_lookups', 'system_lookup_groups',
  'system_lookup_categories', 'onboarding_drafts', 'import_templates',
  'import_runs', 'report_definitions', 'report_templates', 'report_schedules',
  'email_templates', 'uploaded_files', 'generated_documents',
  'code_set_raw_artifacts',
  'fire_boundaries',
  'system_inspection_templates',
  'audit_logs',
  'system_forms',
  'background_jobs',
  // LM-013: Legacy migration tables (server-authoritative mirrors, read-only client sync)
  'legacy_migration_receipts',
  'legacy_migration_runs',
  'legacy_migration_manifest_overrides',
  // LM-020: Schema manifest — one doc per legacy table, admin-shaped table/FK catalog
  'legacy_schema_manifest_tables',
  // G-086: Dataset Template System (§3.11.6) — server-authoritative, never client-pushed
  'legacy_migration_dataset_templates',
  // Clusters registry — server-authoritative, read-only client sync
  'clusters',
  // AI model registry — server-authoritative, read-only client sync
  'ai_models',
  'ai_task_assignments',
  // Phase 5.10 — local cache only, NOT synced via the standard pipeline
  'api_usage_cache',
  // Regrid master-cache file registry — synced from system MongoDB geoCacheFiles
  'geo_cache_files',
  // #737 Phase 6 — scheduling catalogs (server-authoritative pull-only mirrors)
  'system_inspection_programs',
  'system_schedule_rule_catalog',
] as const;

function buildSystemDatabase(dexie: Dexie): SystemDatabase {
  const cols = {} as Record<string, DexieCollection>;
  for (const name of TABLE_NAMES) {
    // Plain Dexie.Table — no encryption for system admin data
    cols[name] = new DexieCollection(dexie.table(name) as any);
  }
  return {
    ...(cols as SystemCollections),
    collections: cols as SystemCollections,
    close: () => { dexie.close(); return Promise.resolve(); },
  };
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------
let dbPromise: Promise<SystemDatabase> | null = null;
let rawDexie: Dexie | null = null;

/**
 * @process GetSystemDb
 *
 * Steps:
 * 1. Open the singleton Dexie database on first access.
 * 2. Reuse the existing promise for subsequent callers.
 *
 * Inputs:
 * - None.
 *
 * Outputs:
 * - The system database wrapper.
 *
 * Failure Handling:
 * - Propagates Dexie open failures to the caller.
 *
 * Idempotency:
 * - REQUIRED
 */
export function getSystemDb(): Promise<SystemDatabase> {
  if (!dbPromise) {
    dbPromise = openSystemDb().catch((err) => {
      // Clear the cached promise so the next call can retry. Without this,
      // a single failed open (e.g. a Dexie version upgrade that fails due
      // to duplicate data) permanently breaks the app until a full page
      // refresh. The caller still receives the rejection.
      dbPromise = null;
      throw err;
    });
  }
  return dbPromise;
}

/**
 * @process GetSystemDexieRaw
 *
 * Steps:
 * 1. Return the raw Dexie instance after the system DB has been opened.
 * 2. Return null when the singleton has not been initialized yet.
 *
 * Inputs:
 * - None.
 *
 * Outputs:
 * - The raw Dexie instance or null.
 *
 * Failure Handling:
 * - Returns null instead of throwing when the DB is unopened.
 *
 * Idempotency:
 * - REQUIRED
 */
export function getSystemDexieRaw(): Dexie | null {
  return rawDexie;
}

/**
 * @process IsSystemDbOpen
 *
 * Steps:
 * 1. Inspect whether the system DB singleton promise has been created.
 *
 * Inputs:
 * - None.
 *
 * Outputs:
 * - Boolean indicating whether open is in-flight or complete.
 *
 * Failure Handling:
 * - Not applicable.
 *
 * Idempotency:
 * - REQUIRED
 */
export function isSystemDbOpen(): boolean {
  return dbPromise !== null;
}

// indexedDB's version-upgrade open has NO native timeout — if another tab
// or connection still holds an older version of this database open, the
// browser fires 'blocked' and the open() promise simply never resolves.
// Without a bounded timeout here, that silently strands the entire app in
// 'initializing' forever with zero error surfaced anywhere (see local-db
// wipe-and-rebuild blocked-delete regression for the sibling bug on the
// delete path). 10s comfortably exceeds normal upgrade time.
const OPEN_TIMEOUT_MS = 10_000;

async function openSystemDb(): Promise<SystemDatabase> {
  const dexie = createDexieInstance();
  dexie.on('blocked', () => {
    console.warn(
      '[systemDb] IDB upgrade blocked — another tab is holding an older version of firesquirrel-system. ' +
      'Close all other tabs using this app and reload.',
    );
  });
  // If the timeout wins the race, dexie.open() may still be pending and could
  // resolve later, leaving an orphaned IDB connection that blocks future opens.
  // Track the timeout state so we can close any connection that establishes late.
  let timedOut = false;
  const openPromise = dexie.open().then(() => {
    if (timedOut) {
      try { dexie.close(); } catch { /* instance already closed */ }
    }
  });
  try {
    await Promise.race([
      openPromise,
      new Promise<never>((_, reject) => setTimeout(
        () => reject(new Error(
          `Timed out opening firesquirrel-system after ${OPEN_TIMEOUT_MS}ms — likely blocked by another ` +
          'open tab/connection holding an older schema version. Close other tabs of this app and reload.',
        )),
        OPEN_TIMEOUT_MS,
      )),
    ]);
  } catch (err) {
    timedOut = true;
    // Abort any in-flight open and release any connection that does get established.
    try { dexie.close(); } catch { /* instance may not be open yet */ }
    throw err;
  }
  rawDexie = dexie;
  return buildSystemDatabase(dexie);
}

/**
 * @process DestroySystemDb
 *
 * Steps:
 * 1. Await the current singleton database if it exists.
 * 2. Close the underlying Dexie instance.
 * 3. Clear the cached singleton reference.
 *
 * Inputs:
 * - None.
 *
 * Outputs:
 * - None.
 *
 * Failure Handling:
 * - Propagates close failures to the caller.
 *
 * Idempotency:
 * - REQUIRED
 */
export async function destroySystemDb(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    await db.close();
    dbPromise = null;
    rawDexie = null;
  }
}

export { DexieCollection };
